import { type Theme } from "@mui/material/styles";

// ----------------------------------------------------------------------

export default function Input(theme: Theme): any {
  return {
    MuiInputBase: {
      styleOverrides: {
        root: {
          "&.MuiOutlinedInput-root": {
            borderRadius: theme.shape.borderRadiusMd,
          },
          "&.Mui-disabled": {
            backgroundColor: theme.palette.action.disabledBackground,
          },
        },
        input: {
          "&::placeholder": {
            opacity: 1,
            color: theme.palette.text.disabled,
            fontWeight: 400,
          },
          // Override Chrome autofill styles
          "&:-webkit-autofill": {
            WebkitBoxShadow:
              theme.palette.mode === "dark"
                ? `0 0 0 100px ${theme.palette.background.secondary} inset`
                : `0 0 0 100px ${theme.palette.background.default} inset`,
            WebkitTextFillColor: theme.palette.text.primary,
            caretColor: theme.palette.text.primary,
            transition: "background-color 5000s ease-in-out 0s",
          },
          "&:-webkit-autofill:hover": {
            WebkitBoxShadow:
              theme.palette.mode === "dark"
                ? `0 0 0 100px ${theme.palette.background.secondary} inset`
                : `0 0 0 100px ${theme.palette.background.default} inset`,
            WebkitTextFillColor: theme.palette.text.primary,
          },
          "&:-webkit-autofill:focus": {
            WebkitBoxShadow:
              theme.palette.mode === "dark"
                ? `0 0 0 100px ${theme.palette.background.secondary} inset`
                : `0 0 0 100px ${theme.palette.background.default} inset`,
            WebkitTextFillColor: theme.palette.text.primary,
          },
          "&:-webkit-autofill:active": {
            WebkitBoxShadow:
              theme.palette.mode === "dark"
                ? `0 0 0 100px ${theme.palette.background.secondary} inset`
                : `0 0 0 100px ${theme.palette.background.default} inset`,
            WebkitTextFillColor: theme.palette.text.primary,
          },
        },
        formControl: {},
        multiline: {
          padding: `${theme.spacing(1)} 0px !important`,
        },
      },
    },
    MuiInput: {
      styleOverrides: {
        underline: {
          "&:before": {
            borderBottomColor: theme.palette.grey[500_56],
          },
        },
      },
    },
    MuiFilledInput: {
      styleOverrides: {
        root: {
          backgroundColor: theme.palette.grey[500_12],
          "&:hover": {
            backgroundColor: theme.palette.grey[500_16],
          },
          "&.Mui-focused": {
            backgroundColor: theme.palette.action.focus,
          },
          "&.Mui-disabled": {
            backgroundColor: theme.palette.action.disabledBackground,
          },
        },
        input: {
          // Override Chrome autofill styles for filled inputs
          "&:-webkit-autofill": {
            WebkitBoxShadow:
              theme.palette.mode === "dark"
                ? `0 0 0 100px ${theme.palette.background.secondary} inset`
                : `0 0 0 100px ${theme.palette.grey[200]} inset`,
            WebkitTextFillColor: theme.palette.text.primary,
          },
          "&:-webkit-autofill:hover": {
            WebkitBoxShadow:
              theme.palette.mode === "dark"
                ? `0 0 0 100px ${theme.palette.background.secondary} inset`
                : `0 0 0 100px ${theme.palette.grey[200]} inset`,
            WebkitTextFillColor: theme.palette.text.primary,
          },
          "&:-webkit-autofill:focus": {
            WebkitBoxShadow:
              theme.palette.mode === "dark"
                ? `0 0 0 100px ${theme.palette.background.secondary} inset`
                : `0 0 0 100px ${theme.palette.grey[200]} inset`,
            WebkitTextFillColor: theme.palette.text.primary,
          },
          "&:-webkit-autofill:active": {
            WebkitBoxShadow:
              theme.palette.mode === "dark"
                ? `0 0 0 100px ${theme.palette.background.secondary} inset`
                : `0 0 0 100px ${theme.palette.grey[200]} inset`,
            WebkitTextFillColor: theme.palette.text.primary,
          },
        },
        underline: {
          "&:before": {
            borderBottomColor: theme.palette.grey[500_56],
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: "6px !important",
          borderColor: theme.palette.border.borderMain,
          fontWeight: 500,
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: theme.palette.border.borderMain,

            transition: theme.transitions.create("borderColor", {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.leavingScreen,
            }),
          },
          "&.Mui-disabled": {
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: theme.palette.action.disabledBackground,
            },
          },
        },
        input: {
          height: theme.spacing(7),
          padding: `0px ${theme.spacing(2)} `,
          borderRadius: "6px !important",
          // Override Chrome autofill styles for outlined inputs
          "&:-webkit-autofill": {
            WebkitBoxShadow:
              theme.palette.mode === "dark"
                ? `0 0 0 100px ${theme.palette.background.secondary} inset`
                : `0 0 0 100px ${theme.palette.background.default} inset`,
            WebkitTextFillColor: theme.palette.text.primary,
            borderRadius: "6px !important",
          },
          "&:-webkit-autofill:hover": {
            WebkitBoxShadow:
              theme.palette.mode === "dark"
                ? `0 0 0 100px ${theme.palette.background.secondary} inset`
                : `0 0 0 100px ${theme.palette.background.default} inset`,
            WebkitTextFillColor: theme.palette.text.primary,
          },
          "&:-webkit-autofill:focus": {
            WebkitBoxShadow:
              theme.palette.mode === "dark"
                ? `0 0 0 100px ${theme.palette.background.secondary} inset`
                : `0 0 0 100px ${theme.palette.background.default} inset`,
            WebkitTextFillColor: theme.palette.text.primary,
          },
          "&:-webkit-autofill:active": {
            WebkitBoxShadow:
              theme.palette.mode === "dark"
                ? `0 0 0 100px ${theme.palette.background.secondary} inset`
                : `0 0 0 100px ${theme.palette.background.default} inset`,
            WebkitTextFillColor: theme.palette.text.primary,
          },
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        inputRoot: {
          padding: "0px",
        },
        option: {
          padding: "4px 8px !important",
        },
      },
    },
  };
}
